import usingGrapesImg1 from '../assets/images/faq-images/using-grapes-1.png'
import assementImg1 from '../assets/images/faq-images/assessment-1.png'
import assementImg2 from '../assets/images/faq-images/assessment-2.png'
import assementImg3 from '../assets/images/faq-images/assessment-3.png'
import assementImg4 from '../assets/images/faq-images/assessment-4.png'

interface FaqContent {
  title: string
  content: string
  images?: string[]
  logged: boolean
  leader: boolean
  allUsers?: boolean
}

export interface FaqCategory {
  id: string
  name: string
  content: FaqContent[]
}

export const ContentList: FaqCategory[] = [
  {
    id: 'about',
    name: 'About Grapes',
    content: [
      {
        title: 'What is **Grapes for 10x Talent Assessments**?',
        content:
          'Grapes is an IT talent assessment tool focused on identifying and developing 10x engineers and 9x engineers. It was developed by The Agile Monkeys in partnership with Optum Financial Technology.',
        logged: false,
        leader: false,
        allUsers: true,
      },
      {
        title: 'Who completes the **talent assessments**?',
        content: `Managers and team leaders complete the assessments for their direct reports. Leaders can also delegate assessments to other leaders within their hierarchy who are familiar with a person's work.`,
        logged: false,
        leader: false,
        allUsers: true,
      },
      {
        title: 'Who can be **assessed**?',
        content:
          'Grapes is focused on Engineering roles at Grade Level 29 and above — Lead, Principal, Sr. Principal, and VP-level engineers.',
        logged: false,
        leader: false,
        allUsers: true,
      },
      {
        title: 'How **frequently** should assessments be conducted?',
        content:
          'Managers should complete assessments at least quarterly to facilitate regular development conversations and track progress over time.',
        logged: false,
        leader: false,
        allUsers: true,
      },
      {
        title: 'Who can see the **results of the assessments**?',
        content: `Results can be viewed by the staff member's manager and leaders in their hierarchy. Managers can also download a PDF of the report to share with their team members.`,
        logged: false,
        leader: false,
        allUsers: true,
      },
      {
        title: 'What **skills are assessed**?',
        content:
          'The assessment covers the following categories: Growth Mindset, Communication, Professional Skills, Productivity, AI, and Commitment. Questions are tailored by role and job level based on leveling guides established within Optum.',
        logged: false,
        leader: false,
        allUsers: true,
      },
      {
        title: 'How does the system decide who **10x and 9x engineers** are?',
        content:
          'A **10x Engineer** scores 90% or above overall. They demonstrate deep technical mastery, AI-first thinking, business alignment, autonomy, and strong collaboration, multiplying the output of their entire team.\n\nA **9x Engineer** scores between 83% and 89%. They show strong potential and are on a clear path to top talent.\n\nA **Core** talent scores between 60% and 82%.\n\nA **Mismatch** scores below 60%.',
        logged: false,
        leader: false,
        allUsers: true,
      },
      {
        title: 'How do we **intend to use the results**? What **decisions** do they drive?',
        content:
          'Assessments give managers and leaders a view of individual and team strengths and gaps. They help facilitate development conversations and identify opportunities for growth through training, coaching, project assignments, and more.',
        logged: false,
        leader: false,
        allUsers: true,
      },
      {
        title: 'How do these assessments **impact my annual review**?',
        content:
          'There is no direct correlation between Grapes assessments and the annual review. However, assessments can be used to demonstrate or highlight growth and contributions during the review period.',
        logged: false,
        leader: false,
        allUsers: true,
      },
      {
        title: 'Will I be able to **see my results** and provide feedback?',
        content:
          'Your manager or leader will review the assessments with you via 1:1 conversations, during which you are encouraged to ask questions and provide feedback.',
        logged: false,
        leader: false,
        allUsers: true,
      },
    ],
  },
  {
    id: 'account-password',
    name: 'Account and password',
    content: [
      {
        title: 'How do I **log in** to Grapes 10x Talent Assessment?',
        content: `You can log in using your Optum email address and a temporary password that will be provided to you by email. We are working on SSO integration to simplify the login process, which will eventually replace the current method.\n\nThe first time you log in, you will be prompted to change your password before accessing the platform. Make sure to set a secure password you'll remember.\n\nIf you haven't received your temporary password or are having trouble logging in, please reach out to **[grapes@theagilemonkeys.com](mailto:grapes@theagilemonkeys.com)**.`,
        logged: false,
        leader: false,
      },
      {
        title: 'What do I do if I **forget my password** or want to change it?',
        content: `If you've forgotten your password or would like to change it, click the "Forgot password" button on the login page. This will open a window where you can contact our support team directly by email, and we'll get your access reset as soon as possible.`,
        logged: false,
        leader: false,
      },
      {
        title: 'One of my team members is having **trouble accessing** Grapes 10x Talent Assessment',
        content: `All staff members should have received a temporary password to log in with their Optum email address. On their first login, they will be prompted to set a new password before accessing the platform.\n\nIf your staff member hasn't received their credentials, or their details appear correctly in your team list (name, job title) but they still can't log in, please contact **[grapes@theagilemonkeys.com](mailto:grapes@theagilemonkeys.com)** with their name, email address, and a brief description of the issue so we can resolve it promptly.\n\nNote: We are working on SSO integration, which will eventually replace the current email/password login.`,
        logged: true,
        leader: true,
      },
    ],
  },
  {
    id: 'using-grapes',
    name: 'Using Grapes',
    content: [
      {
        title: 'My staff member has an **incorrect job title/level**',
        content:
          'We use roster data to fill in the job title, but the roster files might not always reflect recent changes.\n\nPlease get in touch with us at **[grapes@theagilemonkeys.com](mailto:grapes@theagilemonkeys.com)** and explain your issue so we can inform the staff team and get this fixed until the roster file is up to date.',
        leader: true,
        logged: true,
      },
      {
        title: `What form should I use if my **title doesn't match** any of the options?`,
        content: `Forms are assigned by Grade Level (GL), not by job title, so don't worry if your title doesn't match the form name exactly. If none of the forms seem relevant to your staff member's role, please contact us at **[grapes@theagilemonkeys.com](mailto:grapes@theagilemonkeys.com)** so we can review your situation together.`,
        leader: true,
        logged: true,
      },
      {
        title: 'Can I conduct assessments **more than once** per person?',
        content: `Yes, there is no limit to the number of assessments you can complete. We recommend conducting assessments at least quarterly to facilitate frequent development conversations and show progress over time. You can conduct assessments more often if you find it helpful for your team's development conversations.`,
        leader: true,
        logged: true,
      },
      {
        title: `I **can't find one of my staff** members on the Assessments page`,
        content: `Staff lists are updated in Grapes every other week using the latest roster file provided by the Staff team. Grapes creates teams according to the reporting lines in the roster file.\n\nYou can find the people who report directly to you under the "My Team" tab in the Assessments page. You can find all staff members on your reporting line in the "All" tab.\n\nIf you can't see someone who reports to you in the Assessments page, please let us know at **[grapes@theagilemonkeys.com](mailto:grapes@theagilemonkeys.com)** so we can fix this while the roster file gets updated.`,
        leader: true,
        logged: true,
        images: [usingGrapesImg1],
      },
      {
        title: 'I would like to **add a new employee** to my Grapes team',
        content: `If you can't wait, please email us at **[grapes@theagilemonkeys.com](mailto:grapes@theagilemonkeys.com)** with the new team member's full name and email address, and we'll add them manually as soon as possible.`,
        leader: true,
        logged: true,
      },
      {
        title: 'How can I **delegate an assessment**?',
        content: `You may want another leader familiar with a person's work to complete an assessment for additional perspective, or to delegate specific assessments to senior people in your own teams who work more closely with some of your staff members.\n\nIf that's the case, in the Assessments page, click on the "+" button in the "Reviewer" column and we'll process your request.`,
        leader: true,
        logged: true,
      },
      {
        title: 'I have people on **Assessments that are not part of my team**',
        content:
          'If your Assessments page shows employees who are not under your hierarchy, please let us know at **[grapes@theagilemonkeys.com](mailto:grapes@theagilemonkeys.com)** so we can fix this while the roster file gets updated.',
        leader: true,
        logged: true,
      },
    ],
  },
  {
    id: 'assessments',
    name: 'Conducting assessments',
    content: [
      {
        title: 'How can I **start an assessment**?',
        content:
          'You can start a new assessment by navigating to the **Assessments** page on the main navigation.\n\nUse the search box to find the person you want to create an assessment for, or find them in the table below. Click on their name or the "Add review" button directly to start the assessment.',
        logged: true,
        leader: true,
        images: [assementImg1],
      },
      {
        title: 'Which **assessment form** should I choose?',
        content: `Grapes will automatically suggest the most appropriate form based on the person's role and job level. You should confirm the selection before proceeding.\n\nIf none of the forms are relevant to your staff member's role, please contact us at **[grapes@theagilemonkeys.com](mailto:grapes@theagilemonkeys.com)** so we can review your case together.`,
        logged: true,
        leader: true,
      },
      {
        title: `I finished all the fields in the form, but I still **can't click Publish.**`,
        content: `Please make sure you have completed all fields in each of the sections, including confirming the score at the end of each **Category** section, and the **Final Step**.\n\nYou can use the sidebar navigation to identify unfinished sections. Once a section is complete, you will see a green check icon and the word "Complete" below the finished category. If it's still missing information, you will see a yellow tag labeled "incomplete" below the unfinished category.\n\nIf you are still having trouble, please get in touch with us at **[grapes@theagilemonkeys.com](mailto:grapes@theagilemonkeys.com)** and explain your situation.`,
        logged: true,
        leader: true,
        images: [assementImg2],
      },
      {
        title: 'What happens once I **hit the Publish button**?',
        content: `Once you publish a talent assessment, the information will appear in the staff member's individual report and the team's report. If the score qualifies the person as a 10x or 9x engineer, they will also appear on the Highlights page.\n\nResults can currently be viewed by the staff member's manager and the leaders in their hierarchy. Managers can also download a PDF of the report to share with their team members.`,
        logged: true,
        leader: true,
      },
      {
        title: 'Can I **edit** a published assessment?',
        content: `Yes, you can go back and edit published assessments. Navigate to your team member's individual report (by clicking on their name on any page), select the form you want to edit from the top dropdown, then scroll down and click on "Edit assessment".\n\nPlease note you can only edit assessments you have published yourself.`,
        logged: true,
        leader: true,
        images: [assementImg3, assementImg4],
      },
      {
        title: `If I save an **assessment but don't publish it**, will this information be **visible to other leaders**?`,
        content: `Currently, this information isn't accessible to others. Only the information from assessments you have published will be **visible to upper leaders** in your hierarchy.`,
        logged: true,
        leader: true,
      },
      {
        title: 'Does Grapes **save my progress** automatically?',
        content:
          'Yes. Grapes saves your progress automatically as you fill in the assessment. If you close your browser or navigate away before publishing, your answers will be saved as a draft, and you can pick up where you left off.\n\nYou can also manually save at any point using the **Save draft** button. Drafts are only visible to you. Your answers will not be shared with other leaders until you hit Publish.',
        logged: true,
        leader: true,
      },
      {
        title: 'What does the **AI summary** in the report show?',
        content: `After you publish an assessment, Grapes generates an AI-powered summary of the results. This summary provides a narrative overview of the staff member's strengths and areas for development based on the scores and comments you entered in the assessment.`,
        logged: true,
        leader: true,
      },
      {
        title: 'Can I **delete** a published assessment?',
        content: `Yes, you can delete assessments from a team member's individual report. Navigate to their report by clicking on their name, and use the delete option next to the assessment you want to remove.\n\nPlease note that **deletion is permanent and cannot be undone**. The assessment and all associated data, including the AI summary, will be removed from the report and will no longer be visible to anyone in the hierarchy. If you are unsure, consider editing the assessment instead.`,
        logged: true,
        leader: true,
      },
      {
        title: `I would like to **invite someone from an organization** that doesn't use Grapes 10x Talent Assessment to assess a team member`,
        content:
          'If you need input from someone from a team that is not using Grapes yet, please get in touch with us at **[grapes@theagilemonkeys.com](mailto:grapes@theagilemonkeys.com)** and explain your case.',
        logged: true,
        leader: true,
      },
    ],
  },
]

export const getFaqContentList = (role = ''): FaqCategory[] => {
  const mocked: FaqCategory[] = []

  return mocked
}
